
import React from 'react';
import { useFormContext } from "react-hook-form";
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  CreditCardIcon, 
  CarIcon, 
  Bath,
  RotateCwIcon,
  UtensilsIcon,
  XIcon
} from 'lucide-react';

interface MultiSelectOptionProps {
  value: string;
  onRemove: () => void;
}

const MultiSelectOption = ({ value, onRemove }: MultiSelectOptionProps) => (
  <Badge 
    key={value}
    variant="secondary"
    className="flex items-center gap-1"
  >
    {value}
    <button
      onClick={onRemove}
      className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
    >
      <XIcon size={14} />
    </button>
  </Badge>
);

interface MultiSelectFieldProps {
  label: string;
  icon: React.ReactNode;
  fieldName: string;
  options: { value: string; label: string }[];
  readOnly?: boolean;
}

const MultiSelectField = ({ label, icon, fieldName, options, readOnly = false }: MultiSelectFieldProps) => {
  const { setValue, watch } = useFormContext();
  const selectedValues = watch(`services.${fieldName}`, []);

  const handleMultiSelect = (value: string) => {
    if (readOnly) return;
    
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v: string) => v !== value)
      : [...selectedValues, value];
    setValue(`services.${fieldName}`, newValues);
  };

  return (
    <div className="form-group">
      <Label className="form-label flex items-center gap-2 mb-2">
        {icon}
        {label}
      </Label>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {selectedValues.map((value: string) => (
            <MultiSelectOption 
              key={value}
              value={value}
              onRemove={() => handleMultiSelect(value)}
            />
          ))}
        </div>
        <Select
          onValueChange={handleMultiSelect}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar opciones" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

interface MultiSelectGroupProps {
  readOnly?: boolean;
}

const MultiSelectGroup = ({ readOnly = false }: MultiSelectGroupProps) => {
  const cardOptions = [
    { value: "Débito", label: "Débito" },
    { value: "Crédito", label: "Crédito" },
    { value: "American Express", label: "American Express" },
    { value: "Mastercard", label: "Mastercard" },
    { value: "Visa", label: "Visa" },
    { value: "Transferencia", label: "Transferencia" },
    { value: "QR", label: "QR" },
  ];

  const parkingOptions = [
    { value: "Techada", label: "Techada" },
    { value: "Playa de estacionamiento", label: "Playa de estacionamiento" },
    { value: "Cubierta", label: "Cubierta" },
    { value: "Con seguridad", label: "Con seguridad" }
  ];

  const poolOptions = [
    { value: "Climatizada", label: "Climatizada" },
    { value: "Techada", label: "Techada" },
    { value: "Al aire libre", label: "Al aire libre" },
    { value: "Con solarium", label: "Con solarium" },
    { value: "Con guardavidas", label: "Con guardavidas" }
  ];

  const recreationalSportsOptions = [
    { value: "Cancha de fútbol", label: "Cancha de fútbol" },
    { value: "Cancha de tenis", label: "Cancha de tenis" },
    { value: "Cancha de pádel", label: "Cancha de pádel" },
    { value: "Sala de juegos", label: "Sala de juegos" },
    { value: "Mesa de ping pong", label: "Mesa de ping pong" },
    { value: "Mesa de pool", label: "Mesa de pool" }
  ];

  const specialMenusOptions = [
    { value: "Celíacos", label: "Celíacos" },
    { value: "Veganos", label: "Veganos" },
    { value: "Vegetarianos", label: "Vegetarianos" }
  ];

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-card">
      <h3 className="text-lg font-medium">Servicios Múltiples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MultiSelectField 
          label="Tarjetas" 
          icon={<CreditCardIcon size={16} />} 
          fieldName="cards" 
          options={cardOptions}
          readOnly={readOnly}
        />
        
        <MultiSelectField 
          label="Cochera" 
          icon={<CarIcon size={16} />} 
          fieldName="parking" 
          options={parkingOptions}
          readOnly={readOnly}
        />
        
        <MultiSelectField 
          label="Piscina" 
          icon={<Bath size={16} />} 
          fieldName="pool" 
          options={poolOptions}
          readOnly={readOnly}
        />
        
        <MultiSelectField 
          label="Recreativo/Deportivo" 
          icon={<RotateCwIcon size={16} />} 
          fieldName="recreationalSports" 
          options={recreationalSportsOptions}
          readOnly={readOnly}
        />
        
        <MultiSelectField 
          label="Menús especiales" 
          icon={<UtensilsIcon size={16} />} 
          fieldName="specialMenus" 
          options={specialMenusOptions}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export default MultiSelectGroup;
